import { useState, useEffect, useRef } from 'react';
import './Register.css';
import { roles } from '../common/consts';
import Toast from '../components/Toast';
import { apiService } from '../services/api';

// Sponsor-specific Discord invites (keys are lowercased, spaces stripped)
const SPONSOR_DISCORDS = {
  'ryugear': 'https://discord.gg/82Zvxkr5xm', // TODO: replace with real invite
};
const getSponsorDiscord = (name) => SPONSOR_DISCORDS[(name || '').toLowerCase().replace(/\s+/g, '')];

const useCountdown = (targetDateISO) => {
  const getTimeLeft = () => {
    if (!targetDateISO) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    const diff = new Date(targetDateISO) - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
      done: false,
    };
  };
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);
  useEffect(() => {
    setTimeLeft(getTimeLeft());
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDateISO]);
  return timeLeft;
};

function Register() {
  const STATIC_SOCIAL = {
    id: 'static-pg-esports',
    name: 'PG Esports Club',
    instagram: 'https://www.instagram.com/pgesports.vpl/',
    website: 'https://www.instagram.com/pgesports.vpl/',
    logo:'/images/PG_SOCILAS_logo.webp',
    is_active: true,
    discord: 'https://discord.gg/Bk3u2AczWA',
  };

  const [currentSeason, setCurrentSeason] = useState(null);
  const originalBodyClass = useRef('');
  const [formData, setFormData] = useState({
    valo_id: '',
    discord_id: '',
    game_name: '',
    tracker: '',
    instagram_handle: '',
    name: '',
    email: '',
    phone_num: '',
    peak_rank: '',
    cur_rank: '',
    prim_role: '',
    sec_role: '',
    photo: '',
    state: '',
    city: '',
    availability_note: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [clickedSponsors, setClickedSponsors] = useState({});
  const [toast, setToast] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPhotoGuide, setShowPhotoGuide] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registeredName, setRegisteredName] = useState('');
  const successRef = useRef(null);

  // Countdown reads from Supabase via currentSeason — no hardcoded dates.
  // Update registration_start_date / registration_end_date on the Season row
  // in Supabase and it reflects here instantly with no redeploy.
  const regStartDate = currentSeason?.registration_start_date || null;
  const regEndDate   = currentSeason?.registration_end_date   || null;
  const nowTs        = Date.now();
  const regUpcoming  = regStartDate ? nowTs < new Date(regStartDate).getTime() : false;
  const regOpen      = regStartDate && regEndDate
    ? nowTs >= new Date(regStartDate).getTime() && nowTs < new Date(regEndDate).getTime()
    : false;
  const deadlineTarget = regUpcoming ? regStartDate : regOpen ? regEndDate : null;
  const deadlineCountdown = useCountdown(deadlineTarget);

  const noAutoCapProps = {
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type, id: Date.now() });
  };

  const hideToast = () => {
    setToast(null);
  };

  useEffect(() => {
    const bodyElement = document.querySelector('body');
    originalBodyClass.current = bodyElement.className;
    bodyElement.className = 'no-bg';
    const loadCurrentSeason = async () => {
      try {
        const { default: useStore } = await import('../store/store');
        const store = useStore.getState();
        if (store.currentSeason) {
          setCurrentSeason(store.currentSeason);
        }
      } catch (error) {
        console.error('Error loading current season:', error);
      }
    };
    loadCurrentSeason();
    return () => {
      bodyElement.className = originalBodyClass.current;
    };
  }, []);

  useEffect(() => {
    const loadSponsors = async () => {
      if (!currentSeason?.id) return;
      try {
        const response = await apiService.getSponsors(currentSeason.id, true, true);
        setSponsors(response.data || []);
      } catch (error) {
        console.error('Error loading sponsors:', error);
        setSponsors([]);
      }
    };
    loadSponsors();
  }, [currentSeason?.id]);

  useEffect(() => {
    if (isRegistered && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isRegistered]);

  const activeSponsors = [
    ...sponsors.filter((sponsor) => sponsor.is_active !== false),
    STATIC_SOCIAL,
  ];

  const requiredSocialLinks = activeSponsors.flatMap((sponsor) => {
    const links = [];
    if (sponsor.instagram) links.push(`${sponsor.id}-instagram`);
    if (getSponsorDiscord(sponsor.name)) links.push(`${sponsor.id}-discord`);
    return links;
  });

  const allSponsorsClicked =
    requiredSocialLinks.length > 0 &&
    requiredSocialLinks.every((id) => clickedSponsors[id]);


  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormData((prev) => ({ ...prev, [name]: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.valo_id.trim()) newErrors.valo_id = 'Valorant ID is required';
    if (!formData.discord_id.trim()) newErrors.discord_id = 'Discord ID is required';
    if (!formData.game_name.trim()) newErrors.game_name = 'Game name is required';
    if (!formData.tracker.trim()) newErrors.tracker = 'Tracker URL is required';
    if (!formData.instagram_handle.trim()) newErrors.instagram_handle = 'Instagram handle is required';
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone_num.trim()) newErrors.phone_num = 'Phone number is required';
    if (!formData.prim_role) newErrors.prim_role = 'Primary role is required';
    if (!formData.sec_role) newErrors.sec_role = 'Secondary role is required';
    if (!formData.photo) newErrors.photo = 'Profile photo is required';
    if (!isTermsAccepted) newErrors.terms = 'You must accept the terms to register';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (formData.phone_num && !/^\d{10}$/.test(formData.phone_num.replace(/\D/g, ''))) {
      newErrors.phone_num = 'Please enter a valid 10-digit phone number';
    }
    if (formData.valo_id && !formData.valo_id.includes('#')) {
      newErrors.valo_id = 'Valorant ID must include # (e.g., Username#1234)';
    }
    const urlPattern = /^https?:\/\/.+/;
    if (formData.tracker && !urlPattern.test(formData.tracker)) {
      newErrors.tracker = 'Please enter a valid URL starting with http:// or https://';
    }
    if (formData.instagram_handle && /\s/.test(formData.instagram_handle)) {
      newErrors.instagram_handle = 'Instagram handle cannot contain spaces';
    }
    if (formData.photo && formData.photo instanceof File) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(formData.photo.type)) {
        newErrors.photo = 'Please upload a valid image file (JPG, JPEG, or PNG)';
      }
      if (formData.photo.size > 5 * 1024 * 1024) {
        newErrors.photo = 'Image file size must be less than 5MB';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── resetForm is defined HERE, outside submitRegistration ──
  const resetForm = () => {
    setFormData({
      valo_id: '',
      discord_id: '',
      game_name: '',
      tracker: '',
      instagram_handle: '',
      name: '',
      email: '',
      phone_num: '',
      peak_rank: '',
      cur_rank: '',
      prim_role: '',
      sec_role: '',
      photo: '',
      state: '',
      city: '',
      availability_note: '',
    });
    setIsTermsAccepted(false);
    setClickedSponsors({});
    setErrors({});
    const fileInput = document.getElementById('photo');
    if (fileInput) fileInput.value = '';
  };

  const submitRegistration = async () => {
    setIsSubmitting(true);
    try {
      let photoUrl = '';
      if (formData.photo && formData.photo instanceof File) {
        try {
          const uploadResponse = await apiService.uploadFile(formData.photo, formData.game_name, currentSeason);
          photoUrl = uploadResponse.data.data.url;
        } catch (uploadError) {
          console.error('Image upload error:', uploadError);
          const errorMessage = uploadError.response?.data?.Message || uploadError.message || 'Failed to upload image';
          showToast(`Image upload failed: ${errorMessage}`, 'error');
          return;
        }
      }

      const registrationData = {
        valo_id: formData.valo_id,
        discord_id: formData.discord_id,
        game_name: formData.game_name,
        tracker: formData.tracker,
        instagram_handle: formData.instagram_handle,
        name: formData.name,
        email: formData.email,
        phone_num: formData.phone_num,
        prim_role: formData.prim_role,
        sec_role: formData.sec_role,
        photo: photoUrl,
        latest_season: currentSeason?.id,
        state: formData.state,
        city: formData.city,
        availability_note: formData.availability_note || null,
      };

      try {
        await apiService.registerPlayer(registrationData);
        showToast('You are a Legend Now!', 'success');
        const submittedName = formData.game_name;
        resetForm();
        setRegisteredName(submittedName);
        setIsRegistered(true);
      } catch (playerError) {
        console.error('Player registration error:', playerError);
        const errorMessage = playerError.response?.data?.Message || playerError.message || 'Registration failed';
        showToast(`Registration failed: ${errorMessage}`, 'error');
      }
    } catch (error) {
      console.error('General error:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'An unexpected error occurred';
      showToast(`Registration failed: ${errorMessage}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirmationModal(true);
  };

  const handleConfirmRegistration = async () => {
    setShowConfirmationModal(false);
    await submitRegistration();
  };

  // ── SUCCESS SCREEN ─────────────────────────────────────────
  if (isRegistered) {
    return (
      <div className="register-container" ref={successRef}>
        <div className="register-content">
          <div className="register-header">
            <h1 className="register-title">You&apos;re In The Pool</h1>
            <p className="register-subtitle">VPL Season 5 — Registration Received</p>
          </div>
          <div className="success-banner">
            <div className="success-icon-wrap">
              <svg className="success-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="32" cy="32" r="30" stroke="#3ce2c2" strokeWidth="2.5" />
                <path d="M18 33L27 42L46 22" stroke="#3ce2c2" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {registeredName && <p className="success-player-name">{registeredName}</p>}
            <p className="success-tagline">Your registration has been submitted to the VPL admin team for review.</p>
          </div>
          <div className="form-section success-section">
            <h2 className="section-title">What Happens Next</h2>
            <div className="success-steps">
              <div className="success-step">
                <div className="step-number">01</div>
                <div className="step-content">
                  <p className="step-title">Admin Review</p>
                  <p className="step-desc">The VPL admin team will verify your Valorant ID, rank, and social accounts. This usually takes a few days after registrations close.</p>
                </div>
              </div>
              <div className="success-step">
                <div className="step-number">02</div>
                <div className="step-content">
                  <p className="step-title">Confirmation</p>
                  <p className="step-desc">Approved players will be notified via Discord and Instagram DM. Keep an eye on your registered handles.</p>
                </div>
              </div>
              <div className="success-step">
                <div className="step-number">03</div>
                <div className="step-content">
                  <p className="step-title">Auction Day</p>
                  <p className="step-desc">Verified players enter the Season 5 player pool for the franchise auction. Team owners bid to build their squads — this is where it all begins.</p>
                </div>
              </div>
              <div className="success-step">
                <div className="step-number">04</div>
                <div className="step-content">
                  <p className="step-title">LAN Finals</p>
                  <p className="step-desc">Season 5 closes with VPL&apos;s Second LAN event. The best teams compete on stage — in person, in front of a crowd.</p>
                </div>
              </div>
              <div className="success-step">
                <div className="step-number">05</div>
                <div className="step-content">
                  <p className="step-title">Match Schedule</p>
                  <p className="step-desc">Weekday matches run at 8:30 PM and 9:30 PM. Weekends have 4 matches from the afternoon. Expect to play 2–3 matches per week maximum — plan your availability accordingly.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="form-section success-section">
            <h2 className="section-title">Stay Connected</h2>
            <p className="success-connect-text">All Season 5 updates — fixtures, auction day, team reveals — drop on our socials first. Make sure you&apos;re following.</p>
            <div className="success-socials">
              <a href="https://www.instagram.com/pgesports.vpl/" target="_blank" rel="noreferrer" className="success-social-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
                Follow on Instagram
              </a>
              <a href="https://discord.gg/Bk3u2AczWA" target="_blank" rel="noreferrer" className="success-social-btn success-social-btn--discord">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.995a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                </svg>
                Join the Discord
              </a>
            </div>
          </div>
          <div className="form-submit" style={{ marginBottom: '3rem' }}>
            <button type="button" className="submit-button" onClick={() => { setIsRegistered(false); setRegisteredName(''); }}>
              Register Another Player
            </button>
          </div>
        </div>
      </div>
    );
  }
  // ── END SUCCESS SCREEN ─────────────────────────────────────

  return (
    <div className="register-container">
      <div className="register-content">

        {/* ── DEADLINE BANNER ──────────────────────────────── */}
        {deadlineTarget && !deadlineCountdown.done && (
          <div className={`reg-deadline-banner ${regUpcoming ? 'reg-deadline-banner--upcoming' : ''}`}>
            <span className="reg-deadline-banner__label">
              {regUpcoming ? 'REGISTRATION OPENS IN' : 'REGISTRATION CLOSES IN'}
            </span>
            <div className="reg-deadline-banner__tiles">
              {[
                { v: deadlineCountdown.days,    u: 'D' },
                { v: deadlineCountdown.hours,   u: 'H' },
                { v: deadlineCountdown.minutes, u: 'M' },
                { v: deadlineCountdown.seconds, u: 'S' },
              ].map(({ v, u }) => (
                <div key={u} className="reg-deadline-banner__tile">
                  <span className="reg-deadline-banner__num">{String(v).padStart(2, '0')}</span>
                  <span className="reg-deadline-banner__unit">{u}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ──────────────────────────────────────────────────── */}

        <div className="register-header">
          <h1 className="register-title">Player Registration</h1>
          <p className="register-subtitle">Join the Valorant Premier Legends</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form" autoCapitalize="none" autoCorrect="off" spellCheck={false}>

          <div className="form-section eligibility-section">
            <h2 className="section-title">Eligibility & Verification</h2>
            <div className="eligibility-list">
              <div>✓ VPL Season 5 Is Completely Free To Enter</div>
              <div>✓ Minimum Rank: Platinum 3</div>
              <div>✓ Follow All Required VPL & Partner Social Media Accounts</div>
              <div>✓ Join All Required Tournament Discord Servers</div>
              <div>✓ Provide Accurate Registration Information</div>
              <div>✓ Social Media Handles & Discord IDs May Be Verified</div>
              <div>✓ Registrations Are Subject To Manual Review By The VPL Administration Team</div>
              <div>✓ FAILURE TO MEET ELIGIBILITY REQUIREMENTS WILL RESULT IN DISQUALIFICATION. ALL REGISTRATIONS ARE VERIFIED BEFORE AUCTION DAY — ANY PLAYER FOUND NON-COMPLIANT WILL BE REMOVED PRIOR TO THEIR AUCTION SET.</div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Gaming Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="valo_id" className="form-label">Valorant ID <span className="required">*</span></label>
                <input type="text" id="valo_id" name="valo_id" value={formData.valo_id} onChange={handleInputChange} {...noAutoCapProps} placeholder="e.g., GameName#Tag" className={`form-input ${errors.valo_id ? 'error' : ''}`} />
                {errors.valo_id && <span className="error-message">{errors.valo_id}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="discord_id" className="form-label">Discord ID <span className="required">*</span></label>
                <input type="text" id="discord_id" name="discord_id" value={formData.discord_id} onChange={handleInputChange} {...noAutoCapProps} placeholder="e.g., username1234" className={`form-input ${errors.discord_id ? 'error' : ''}`} />
                {errors.discord_id && <span className="error-message">{errors.discord_id}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="game_name" className="form-label">Display Name <span className="required">*</span></label>
                <input type="text" id="game_name" name="game_name" value={formData.game_name} onChange={handleInputChange} {...noAutoCapProps} placeholder="Display Name" className={`form-input ${errors.game_name ? 'error' : ''}`} />
                {errors.game_name && <span className="error-message">{errors.game_name}</span>}
              </div>
            </div>
            <div className="form-group full-width">
              <label htmlFor="tracker" className="form-label">Tracker.gg Profile URL <span className="required">*</span></label>
              <input type="url" id="tracker" name="tracker" value={formData.tracker} onChange={handleInputChange} {...noAutoCapProps} placeholder="https://tracker.gg/valorant/profile/..." className={`form-input ${errors.tracker ? 'error' : ''}`} />
              {errors.tracker && <span className="error-message">{errors.tracker}</span>}
              <p style={{ fontSize: '0.92rem', color: '#3ce2c2', fontWeight: 500, marginTop: '16px', marginBottom: '0', fontStyle: 'italic', letterSpacing: '0.5px' }}>
                <span style={{ fontWeight: 700, textTransform: 'uppercase', color: '#e53935' }}>Note:</span> Player in-game names must not be changed after registration, and only appropriate names will be accepted
              </p>
            </div>
            <div className="form-group full-width">
              <label htmlFor="instagram_handle" className="form-label">Instagram Handle <span className="required">*</span></label>
              <input type="text" id="instagram_handle" name="instagram_handle" value={formData.instagram_handle} onChange={handleInputChange} {...noAutoCapProps} placeholder="e.g., yourhandle" className={`form-input ${errors.instagram_handle ? 'error' : ''}`} />
              {errors.instagram_handle && <span className="error-message">{errors.instagram_handle}</span>}
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Personal Information</h2>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="name" className="form-label">Full Name <span className="required">*</span></label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} {...noAutoCapProps} placeholder="e.g., Full Name" className={`form-input ${errors.name ? 'error' : ''}`} />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address <span className="required">*</span></label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} {...noAutoCapProps} placeholder="your.email@example.com" className={`form-input ${errors.email ? 'error' : ''}`} />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone_num" className="form-label">Phone Number <span className="required">*</span></label>
                <input type="tel" id="phone_num" name="phone_num" value={formData.phone_num} onChange={handleInputChange} {...noAutoCapProps} placeholder="9876543210" className={`form-input ${errors.phone_num ? 'error' : ''}`} />
                {errors.phone_num && <span className="error-message">{errors.phone_num}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="state" className="form-label">State <span className="required">*</span></label>
                  <select id="state" name="state" value={formData.state} onChange={handleInputChange} className={`form-input ${errors.state ? 'error' : ''}`}>
                    <option value="">Select state…</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                    <option value="Ladakh">Ladakh</option>
                    <option value="Chandigarh">Chandigarh</option>
                    <option value="Puducherry">Puducherry</option>
                    <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                    <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                    <option value="Lakshadweep">Lakshadweep</option>
                  </select>
                  {errors.state && <span className="error-message">{errors.state}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="city" className="form-label">City <span className="required">*</span></label>
                  <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., Hyderabad" className={`form-input ${errors.city ? 'error' : ''}`} />
                  {errors.city && <span className="error-message">{errors.city}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Role Preferences</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prim_role" className="form-label">Primary Role <span className="required">*</span></label>
                <select id="prim_role" name="prim_role" value={formData.prim_role} onChange={handleInputChange} className={`form-select ${errors.prim_role ? 'error' : ''}`}>
                  <option value="">Select primary role</option>
                  {roles.map((role) => (<option key={role} value={role}>{role}</option>))}
                </select>
                {errors.prim_role && <span className="error-message">{errors.prim_role}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="sec_role" className="form-label">Secondary Role <span className="required">*</span></label>
                <select id="sec_role" name="sec_role" value={formData.sec_role} onChange={handleInputChange} className={`form-select ${errors.sec_role ? 'error' : ''}`}>
                  <option value="">Select secondary role</option>
                  {roles.map((role) => (<option key={role} value={role}>{role}</option>))}
                </select>
                {errors.sec_role && <span className="error-message">{errors.sec_role}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Additional Information</h2>
            <div className="form-group full-width">
              <div className="match-schedule-block">
                <p className="match-schedule-title">Match schedule</p>
                <p className="match-schedule-body">Weekdays: 8:30 PM &amp; 9:30 PM · Weekends: 4 matches from afternoon. You'll play 2–3 matches per week maximum.</p>
              </div>
              <label htmlFor="availability_note" className="form-label">
                Availability Concerns <span className="optional">(Optional)</span>
              </label>
              <textarea
                id="availability_note"
                name="availability_note"
                value={formData.availability_note}
                onChange={handleInputChange}
                placeholder="e.g. College exams in October, can't play Sundays, night shift job..."
                className="form-input form-textarea"
                rows={3}
                maxLength={300}
              />
              <small className="field-hint">Mention any conflicts so team owners can plan accordingly.</small>
            </div>
            <div className="form-group full-width">
              <label className="form-label">Player Photo <span className="required">*</span></label>
              {formData.photo ? (
                <div className="photo-selected">
                  <div className="photo-sel-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3ce2c2" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <span className="photo-selected-name">{formData.photo.name}</span>
                  <button type="button" className="photo-change-btn" onClick={() => setShowPhotoGuide(true)}>Change</button>
                </div>
              ) : (
                <div className={`photo-card-zone ${errors.photo ? 'error' : ''}`} onClick={() => setShowPhotoGuide(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setShowPhotoGuide(true)}>
                  <div className="photo-card-avatar">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3ce2c2" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  </div>
                  <div className="photo-card-text">
                    <div className="photo-card-title">ADD YOUR PLAYER PHOTO</div>
                    <div className="photo-card-sub">Used on your auction card &amp; roster. See photo guide before uploading.</div>
                  </div>
                  <div className="photo-card-arrow">›</div>
                </div>
              )}
              <input type="file" id="photo" name="photo" onChange={handleInputChange} accept=".jpg,.jpeg,.png,image/jpeg,image/jpg,image/png" style={{display:'none'}} />
              {errors.photo && <span className="error-message">{errors.photo}</span>}
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Socials</h2>
            <p className="sponsors-note">Follow and join all accounts below to unlock your registration.</p>
            <div className="sponsors-grid">
              {activeSponsors.length === 0 && <div className="sponsors-empty">Socials will appear here soon.</div>}
              {activeSponsors.flatMap((sponsor) => {
                const links = [];
                if (sponsor.instagram) links.push({ id: `${sponsor.id}-instagram`, name: sponsor.name, logo: sponsor.logo, url: sponsor.instagram, action: 'Follow' });
                const discordUrl = getSponsorDiscord(sponsor.name);
                if (discordUrl) links.push({ id: `${sponsor.id}-discord`, name: `${sponsor.name} Discord`, logo: sponsor.logo, url: discordUrl, action: 'Join' });
                return links;
              }).map((social) => {
                const isClicked = !!clickedSponsors[social.id];
                return (
                  <a key={social.id} href={social.url || '#'} target="_blank" rel="noreferrer" className={`sponsor-card ${isClicked ? 'clicked' : ''}`}
                    onClick={(event) => {
                      if (!social.url) event.preventDefault();
                      setClickedSponsors((prev) => ({ ...prev, [social.id]: true }));
                    }}>
                    {social.logo ? <img src={social.logo} alt={`${social.name} logo`} className="sponsor-logo" /> : <div className="sponsor-fallback">{social.name}</div>}
                    <div className="sponsor-meta"><span className="sponsor-name">{social.name}</span></div>
                    <span className="sponsor-cta">{isClicked ? 'Opened' : social.action}</span>
                  </a>
                );
              })}
            </div>
             
            
          </div>

          <div className="form-section">
            <h2 className="section-title">Terms & Conditions</h2>
            <div className="form-group full-width">
              <label htmlFor="terms" className={`terms-checkbox ${!allSponsorsClicked ? 'disabled' : ''}`}>
                <input type="checkbox" id="terms" name="terms" disabled={!allSponsorsClicked} checked={isTermsAccepted}
                  onChange={(e) => { setIsTermsAccepted(e.target.checked); if (errors.terms) setErrors((prev) => ({ ...prev, terms: '' })); }} />
                <span className="terms-box" aria-hidden="true"></span>
                <span className="terms-text">I confirm that all information provided is accurate and that I meet the VPL Season 5 eligibility requirements.</span>
              </label>
              {errors.terms && <span className="error-message">{errors.terms}</span>}
            </div>
          </div>

          <div className="form-submit">
            <button type="submit" disabled={isSubmitting || !isTermsAccepted} className={`submit-button ${isSubmitting ? 'submitting' : ''}`}>
              {isSubmitting ? (<><span className="spinner"></span>Registering...</>) : ('Register for VPL')}
            </button>
          </div>
        </form>
      </div>

      {/* ── Photo Guide Modal ─────────────────────────────────── */}
      {showPhotoGuide && (
        <div className="photo-guide-overlay" role="dialog" aria-modal="true" onClick={() => setShowPhotoGuide(false)}>
          <div className="photo-guide-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-guide-close" onClick={() => setShowPhotoGuide(false)} aria-label="Close">✕</button>
            <h2 className="photo-guide-title">📸 Photo Requirements</h2>
            <p className="photo-guide-desc">Your photo will be used on your auction card and team roster. Please follow these guidelines so we don't have to follow up individually.</p>
            <div className="photo-guide-grid">
              <div className="photo-guide-card good">
                <div className="photo-guide-badge good-badge">✅ GOOD</div>
                <svg viewBox="0 0 100 120" width="100" height="120" aria-label="Upper body solo photo example">
                  <ellipse cx="50" cy="38" rx="18" ry="20" fill="#3ce2c2"/>
                  <rect x="24" y="57" width="52" height="50" rx="6" fill="#2a9080"/>
                  <rect x="24" y="70" width="22" height="37" rx="4" fill="#238070"/>
                  <rect x="54" y="70" width="22" height="37" rx="4" fill="#238070"/>
                </svg>
                <p>Upper body, face visible, solo</p>
              </div>
              <div className="photo-guide-card good">
                <div className="photo-guide-badge good-badge">✅ GOOD</div>
                <svg viewBox="0 0 100 120" width="100" height="120" aria-label="Full body standing photo example">
                  <ellipse cx="50" cy="28" rx="16" ry="18" fill="#3ce2c2"/>
                  <rect x="30" y="45" width="40" height="38" rx="5" fill="#2a9080"/>
                  <rect x="30" y="45" width="16" height="24" rx="3" fill="#1a6860"/>
                  <rect x="54" y="45" width="16" height="24" rx="3" fill="#1a6860"/>
                  <rect x="33" y="81" width="14" height="34" rx="4" fill="#2a9080"/>
                  <rect x="53" y="81" width="14" height="34" rx="4" fill="#2a9080"/>
                </svg>
                <p>Full body standing, arms crossed or relaxed</p>
              </div>
              <div className="photo-guide-card bad">
                <div className="photo-guide-badge bad-badge">❌ NOT ACCEPTED</div>
                <svg viewBox="0 0 100 120" width="100" height="120" aria-label="Group photo not accepted">
                  <ellipse cx="22" cy="48" rx="12" ry="13" fill="#666"/>
                  <rect x="11" y="60" width="22" height="36" rx="3" fill="#555"/>
                  <ellipse cx="50" cy="42" rx="14" ry="15" fill="#777"/>
                  <rect x="37" y="56" width="26" height="40" rx="3" fill="#666"/>
                  <ellipse cx="78" cy="48" rx="12" ry="13" fill="#666"/>
                  <rect x="67" y="60" width="22" height="36" rx="3" fill="#555"/>
                  <line x1="5" y1="5" x2="95" y2="115" stroke="#ff4655" strokeWidth="3" opacity="0.5"/>
                  <line x1="95" y1="5" x2="5" y2="115" stroke="#ff4655" strokeWidth="3" opacity="0.5"/>
                </svg>
                <p>Group photo — we can't identify you</p>
              </div>
              <div className="photo-guide-card bad">
                <div className="photo-guide-badge bad-badge">❌ NOT ACCEPTED</div>
                <svg viewBox="0 0 100 120" width="100" height="120" aria-label="Passport photo not accepted">
                  <rect x="28" y="10" width="44" height="90" rx="4" fill="#1e2830" stroke="#445" strokeWidth="1"/>
                  <ellipse cx="50" cy="40" rx="11" ry="12" fill="#555"/>
                  <rect x="39" y="51" width="22" height="26" rx="3" fill="#4a5560"/>
                  <rect x="31" y="82" width="38" height="3" rx="1" fill="#334"/>
                  <rect x="31" y="88" width="38" height="3" rx="1" fill="#334"/>
                  <rect x="31" y="94" width="24" height="3" rx="1" fill="#334"/>
                  <line x1="5" y1="5" x2="95" y2="115" stroke="#ff4655" strokeWidth="3" opacity="0.5"/>
                  <line x1="95" y1="5" x2="5" y2="115" stroke="#ff4655" strokeWidth="3" opacity="0.5"/>
                </svg>
                <p>Passport / ID photo — too small for auction cards</p>
              </div>
            </div>
            <div className="photo-guide-rules">
              <h3>Quick rules:</h3>
              <ul>
                <li>✅ Solo — only you in the frame</li>
                <li>✅ Face clearly visible</li>
                <li>✅ Full body or upper body shot</li>
                <li>✅ JPG or PNG, max 5MB</li>
                <li>❌ No group photos</li>
                <li>❌ No passport / ID photos</li>
                <li>❌ No heavily filtered or cartoon photos</li>
                <li>💡 Any background is fine — we cut it out</li>
              </ul>
            </div>
            <button className="photo-guide-confirm" onClick={() => {
              setShowPhotoGuide(false);
              setTimeout(() => document.getElementById('photo')?.click(), 100);
            }}>
              Got it — Upload My Photo
            </button>
          </div>
        </div>
      )}

      {showConfirmationModal && (
        <div className="confirmation-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
          <div className="confirmation-modal">
            <h2 id="confirm-title" className="confirmation-modal-title">Ready to Submit?</h2>
            <ul className="confirmation-modal-list">
              <li>Your registration information is accurate.</li>
              <li>Your social media and Discord details belong to you.</li>
              <li>You meet the VPL Season 5 eligibility requirements.</li>
            </ul>
            <p className="confirmation-modal-text confirmation-modal-warning">All registrations are subject to review by the VPL Administration Team.</p>
            <p className="confirmation-modal-text">Once submitted, your registration will be added to the Season 5 player pool for review.</p>
            <p className="confirmation-modal-question">Continue with registration?</p>
            <div className="confirmation-modal-actions">
              <button type="button" className="confirmation-button confirmation-button-cancel" onClick={() => setShowConfirmationModal(false)} disabled={isSubmitting}>Cancel</button>
              <button type="button" className="confirmation-button confirmation-button-submit" onClick={handleConfirmRegistration} disabled={isSubmitting}>Submit Registration</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </div>
  );
}

export default Register;